import type { ProductSection } from "./productContent";

export const PRIVACY_SECURITY_SECTIONS: readonly ProductSection[] = [
  {
    title: "No phone number required",
    body: "Create a Profile and join the right Space without making a mobile number the centre of your identity."
  },
  {
    title: "No contact upload",
    body: "Booply does not need your address book to build the group. Joining happens through QR codes, invite codes, links and admin approval."
  },
  {
    title: "Text-only",
    body: "Messages are text-only and capped at 800 characters, keeping updates focused and avoiding the storage and moderation burden of media."
  },
  {
    title: "Short retention",
    body: "Free and Plus Spaces clear messages after a week by default. Paid Spaces can choose a week, two weeks, or a month, so group history stays intentionally short."
  },
  {
    title: "Together and connected",
    body: "Booply is local when the group is together and connected when people are apart. The bridge is intended to route encrypted envelopes, with production security depending on the real crypto provider."
  },
  {
    title: "Delivery receipts only",
    body: "Senders can understand whether messages reached devices without turning delivery into read receipts or seen-by lists."
  },
  {
    title: "Report and block",
    body: "Members can use safety tools when something goes wrong. Reporting is an exception to ordinary message handling because selected content may be submitted for review."
  },
  {
    title: "Security roadmap",
    body: "Booply’s roadmap includes threat model updates, protocol documentation, dependency review, implementation review, and later external assessment before stronger security claims are made."
  }
];

export const WHITEPAPER_SECTIONS: readonly ProductSection[] = [
  {
    title: "Introduction",
    body: "Booply is private coordination for groups that meet in real life: organisers, clubs, teams, events and communities that need Rooms without a public social network."
  },
  {
    title: "Product model",
    body: "The model is built for real-world groups: Spaces hold the group, Rooms organise operations, Profiles identify people, Devices represent app installations, and Members connect Profiles to Spaces."
  },
  {
    title: "Identity model",
    body: "Booply uses Profiles and Devices instead of phone-number identity. This supports future device keys and recovery flows while avoiding address-book based growth."
  },
  {
    title: "Joining Spaces",
    body: "People join Spaces through QR codes, invite codes, invite links, or join requests. Admin approval is the default posture for private Spaces."
  },
  {
    title: "Together",
    body: "Together is intended for approved local group contexts where devices are close enough to communicate directly. It is not a public discovery layer or global profile directory."
  },
  {
    title: "Connected",
    body: "Booply’s managed bridge helps when people are apart or local delivery is unreliable. It is designed to route encrypted envelopes and operational state needed for delivery."
  },
  {
    title: "Message encryption plan",
    body: "Booply’s first production crypto model is intended to encrypt ordinary messages before Relay. The Relay path handles envelope metadata needed for delivery, while real message security depends on the future crypto provider implementation and review."
  },
  {
    title: "Message retention and deletion",
    body: "Retention is short by design: Free and Plus Spaces use 7 days, while paid Spaces can use 7, 14, or 30 days. Deletion and expiry are implemented as product and backend rules, without promising control over member screenshots or copied text."
  },
  {
    title: "Reports and moderation",
    body: "Reporting is an exception to ordinary message handling. When a member reports content, selected content and context may be submitted to support safety review and moderation workflows."
  },
  {
    title: "Billing and paid Space slots",
    body: "Plus supports more personal Spaces. Spaces provides one assignable paid Space slot, and paid Spaces do not count against Free or Plus personal Space creation limits."
  },
  {
    title: "Metadata and privacy",
    body: "Booply needs some metadata to run the service, including account, device, membership, invite, delivery, billing, moderation, retention, and operational records."
  },
  {
    title: "What Booply does not require",
    body: "Booply does not require phone numbers, contact upload, media messages, DMs, read receipts, or public profile discovery for the core product."
  },
  {
    title: "Security limitations",
    body: "Booply reduces what the backend can read by default, but it cannot control what members do on their own devices, and future cryptographic claims depend on implementation review."
  },
  {
    title: "Security review roadmap",
    body: "The roadmap includes protocol documentation, threat model updates, dependency checks, implementation review, operational hardening, abuse testing, and later external assessment."
  },
  {
    title: "Open-source attribution",
    body: "Booply is closed source with transparent docs and respectful attribution. Booply’s local mesh layer is inspired by, and where applicable incorporates, public-domain work from the bit.chat project."
  },
  {
    title: "Conclusion",
    body: "Booply’s privacy and security approach is practical and product-shaped: start in the room, collect less, keep messages text-only, keep retention short and make the trade-offs clear."
  }
];
